import { useEffect, useMemo, useState } from 'react'
import { createdMatches, distanceFrom, getProfile, mockJoinedMatch, mockMatches, mockMyMatch, withOverrides, type MockMatch, type MatchType } from '@/shared/mock/data'
import { getPlayerActions } from '@/shared/player-actions'
import { bandFor, type LevelBand } from '@/shared/levels'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useUpcomingMatches } from './use-matches'
import { useBlockedUids } from '@/features/contactos/use-contacts-real'
import { useMyProfile } from '@/features/perfil/use-my-profile'

// Discovery feed hook — owns the 4 states + the discovery axes. Under real
// auth the base set is the `matches` city query (upcoming, public, active,
// seats free); under the dev bypass the certified mock pipeline stays intact.
// All facets narrow client-side over the same view shape in both branches.
export type FeedStatus = 'loading' | 'loaded' | 'empty' | 'error'
export type TimeWindow = 'todos' | 'hoy' | 'manana' | 'semana'
export type TypeFilter = 'todos' | MatchType
export type PositionFilter = 'todos' | 'Derecha' | 'Revés'
export type GenderFilter = 'todos' | 'mixed' | 'women' | 'men'

export function matchesTime(m: MockMatch, when: TimeWindow): boolean {
  if (when === 'hoy') return m.dayLabel === 'Hoy'
  if (when === 'manana') return m.dayLabel === 'Mañana'
  return true // 'semana' + 'todos' — the mock feed spans this week
}

export function matchesQuery(m: MockMatch, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return m.place.toLowerCase().includes(q) || m.city.toLowerCase().includes(q)
}

// A band filter matches when the ranges overlap; a match with no level
// requirement (Indiferente) always fits.
export function matchesLevel(m: MockMatch, band: LevelBand | null): boolean {
  if (!band) return true
  if (m.levelMin == null || m.levelMax == null) return true
  return m.levelMin < band.max && m.levelMax > band.min
}

// Category filter is EQUALITY on the restricted categories; 'todos' shows all.
export function matchesGender(m: MockMatch, g: GenderFilter): boolean {
  if (g === 'todos') return true
  return (m.genderCategory ?? 'open') === g
}

// Position: a match seeking "Indiferente" fits any player.
export function matchesPosition(m: MockMatch, pos: PositionFilter): boolean {
  if (pos === 'todos') return true
  return m.position === pos || m.position === 'Indiferente'
}


// DEV-only: `?estado=error` forces the error state so the 4-states contract is
// verifiable in walks/reviews (the mock never fails on its own).
function forcedError(): boolean {
  return new URLSearchParams(window.location.search).get('estado') === 'error'
}


// Filters survive push/pop navigation (browse -> detail -> back is THE seeker
// workflow): the axes live at module scope and each mount rehydrates from them.
const feedMemory = {
  query: '',
  /** null = the player never picked — each mount derives from their profile
   *  city (fallback Granada). An explicit header pick wins for the session. */
  location: null as string | null,
  radiusKm: 10,
  timeWindow: 'todos' as TimeWindow,
  typeFilter: 'todos' as TypeFilter,
  levelBand: null as LevelBand | null,
  /** false = the player has never touched the Nivel filter, so the feed derives
   *  it from THEIR OWN level (see the effect below). Any explicit pick —
   *  including "Todos" (null) — flips this and wins for the session. Mirrors
   *  the `location` "null = never picked" idea, but level needs a separate flag
   *  because null is itself a meaningful choice here. */
  levelBandPicked: false,
  position: 'todos' as PositionFilter,
  gender: 'todos' as GenderFilter,
}

export function usePartidos(initialQuery = '') {
  const [base, setBase] = useState<FeedStatus>('loading')
  const [query, setQueryState] = useState(initialQuery || feedMemory.query)
  const [location, setLocationState] = useState(feedMemory.location ?? getProfile().city ?? 'Granada')
  const [radiusKm, setRadiusKmState] = useState(feedMemory.radiusKm)
  const [timeWindow, setTimeWindowState] = useState<TimeWindow>(feedMemory.timeWindow)
  const [typeFilter, setTypeFilterState] = useState<TypeFilter>(feedMemory.typeFilter)
  const [levelBand, setLevelBandState] = useState<LevelBand | null>(feedMemory.levelBand)
  const [position, setPositionState] = useState<PositionFilter>(feedMemory.position)
  const [gender, setGenderState] = useState<GenderFilter>(feedMemory.gender)

  const remember = <K extends keyof typeof feedMemory>(key: K, set: (v: (typeof feedMemory)[K]) => void) =>
    (v: (typeof feedMemory)[K]) => {
      feedMemory[key] = v
      set(v)
    }
  const setQuery = remember('query', setQueryState)
  const setLocation = remember('location', setLocationState as (v: string | null) => void) as (
    v: string,
  ) => void
  const setRadiusKm = remember('radiusKm', setRadiusKmState)
  const setTimeWindow = remember('timeWindow', setTimeWindowState)
  const setTypeFilter = remember('typeFilter', setTypeFilterState)
  const setLevelBand = (band: LevelBand | null) => {
    // An explicit pick — never re-derive after this, even to "Todos".
    feedMemory.levelBandPicked = true
    feedMemory.levelBand = band
    setLevelBandState(band)
  }
  const setPosition = remember('position', setPositionState)
  const setGender = remember('gender', setGenderState)

  const upcoming = useUpcomingMatches(location)
  const blockedUids = useBlockedUids()
  const myProfile = useMyProfile()
  const homeClubId = myProfile.data?.homeClubId ?? null
  const [onlyMyClub, setOnlyMyClub] = useState(false)

  // Open the feed on matches that FIT THE PLAYER. The app already knows their
  // level (self-declared at onboarding, then ELO-calibrated from competitive
  // results) and every match carries a level range — but the filter used to
  // default to "all levels", so a casual player got the firehose and had to
  // self-select into a band, which is exactly the judgement they are least
  // equipped to make and the hesitation that stops them joining strangers.
  //
  // This is a DEFAULT, not a lock: the Nivel chip still opens the full band
  // sheet and "Todos" is one tap away. Only applies while the player has never
  // touched the filter — `setLevelBand` flips `levelBandPicked` permanently for
  // the session, including when they pick "Todos".
  const myLevel = AUTH_BYPASS
    ? (getProfile().level as number | null)
    : (myProfile.data?.padel.skillLevel ?? null)
  useEffect(() => {
    if (feedMemory.levelBandPicked || myLevel == null) return
    const band = bandFor(myLevel)
    if (!band) return
    feedMemory.levelBand = band
    setLevelBandState(band)
  }, [myLevel])

  useEffect(() => {
    if (!AUTH_BYPASS) return
    const t = setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
    return () => clearTimeout(t)
  }, [])

  const { matches, hiddenByBlock } = useMemo(() => {
    if (!AUTH_BYPASS) {
      const base = (upcoming.data ?? [])
        .filter((m) => !onlyMyClub || (homeClubId != null && m.padelPlaceId === homeClubId))
        // Home-club matches lead (research F8: liquidity concentrates
        // club-first) — a stable boost, never hiding the rest.
        .sort((a, b) =>
          homeClubId == null
            ? 0
            : Number(b.padelPlaceId === homeClubId) - Number(a.padelPlaceId === homeClubId),
        )
      const beforeBlock = base.filter(
        (m) =>
          (m.distanceKm == null || m.distanceKm <= radiusKm) &&
          matchesTime(m, timeWindow) &&
          (typeFilter === 'todos' || m.matchType === typeFilter) &&
          matchesLevel(m, levelBand) &&
          matchesPosition(m, position) &&
          matchesGender(m, gender) &&
          matchesQuery(m, query),
      )
      const visible = beforeBlock.filter(
        (m) => !(m.organizerUid && blockedUids.has(m.organizerUid)),
      )
      return { matches: visible, hiddenByBlock: beforeBlock.length - visible.length }
    }
    if (base !== 'loaded') return { matches: [], hiddenByBlock: 0 }
    // Location is a real axis: distances are recomputed from the selected
    // centre, then the radius applies (Madrid honestly empties the feed).
    // The feed = EVERY open, active match (incl. own/joined ones once public) —
    // "aparecerá en el feed" must be true. Blocked organizers are filtered out
    // LAST, and the hidden count is kept so an empty result can say why.
    const beforeBlock = [...createdMatches, mockMyMatch, mockJoinedMatch, ...mockMatches]
      .map((m) => withOverrides(m))
      .filter((m) => m.privacy === 'Abierto' && m.status === 'active')
      .map((m) => ({ ...m, distanceKm: distanceFrom(location, m.distanceKm ?? 0) }))
      .filter(
        (m) =>
          (m.distanceKm == null || m.distanceKm <= radiusKm) &&
          matchesTime(m, timeWindow) &&
          (typeFilter === 'todos' || m.matchType === typeFilter) &&
          matchesLevel(m, levelBand) &&
          matchesPosition(m, position) &&
          matchesGender(m, gender) &&
          matchesQuery(m, query),
      )
    const visible = beforeBlock.filter((m) => !getPlayerActions(m.organizer).blocked)
    return { matches: visible, hiddenByBlock: beforeBlock.length - visible.length }
  }, [base, location, radiusKm, timeWindow, typeFilter, levelBand, position, gender, query, upcoming.data, homeClubId, onlyMyClub, blockedUids])

  const status: FeedStatus = AUTH_BYPASS
    ? base === 'loaded' && matches.length === 0
      ? 'empty'
      : base
    : forcedError() || upcoming.isError
      ? 'error'
      : upcoming.isPending
        ? 'loading'
        : matches.length === 0
          ? 'empty'
          : 'loaded'

  function refresh() {
    if (!AUTH_BYPASS) {
      void upcoming.refetch()
      return
    }
    setBase('loading')
    setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
  }

  return {
    status,
    matches,
    hiddenByBlock,
    query,
    setQuery,
    location,
    setLocation,
    radiusKm,
    setRadiusKm,
    timeWindow,
    setTimeWindow,
    typeFilter,
    setTypeFilter,
    levelBand,
    /** True when the band was DERIVED from the player's own level rather than
     *  chosen. Callers must not treat it as an active filter — the player never
     *  set it, so "Quitar filtros" would blame them for a choice they did not
     *  make (caught by the e2e critical path, which then found two competing
     *  "Crear partido" buttons in the empty state). */
    levelBandAuto: levelBand !== null && !feedMemory.levelBandPicked,
    setLevelBand,
    position,
    setPosition,
    gender,
    setGender,
    homeClubId,
    onlyMyClub,
    setOnlyMyClub,
    refresh,
    retry: refresh,
  }
}
